import { EditOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { history } from '@umijs/max';
import { useIntl, useModel } from 'umi';
import type { MenuProps } from 'antd';
import { Avatar, Spin, theme } from 'antd';
import { createStyles } from 'antd-style';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import { useLogout } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import HeaderDropdown from '../HeaderDropdown';
import UserProfileModal from '../UserProfileModal';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  /** Omit to use the default header avatar trigger (styled ring + hover). */
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { user } = useAuthStore();
  const initialState = useModel('@@initialState');
  const { currentUser } = (initialState as any) || {};
  // Prefer Zustand store, fallback to initialState
  const displayName = user?.name || currentUser?.name || 'Admin';
  return <span className="anticon">{displayName}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
    /** First row: account name (disabled item should not look “greyed out”) */
    avatarDropdownMenu: {
      '.account-dropdown-menu-header': {
        opacity: '1 !important',
        cursor: 'default !important',
        color: `${token.colorTextHeading} !important`,
        fontWeight: 600,
        userSelect: 'text',
        // Same visual weight as “정보 수정 / 로그아웃” row icons
        '.ant-dropdown-menu-item-icon, .anticon': {
          color: `${token.colorIcon} !important`,
        },
      },
    },
    /** Header trigger: clear edge, subtle depth, matches icon row height ~48px */
    avatarTrigger: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      padding: 3,
      lineHeight: 0,
      borderRadius: '50%',
      transition: 'box-shadow 0.2s ease, background-color 0.2s ease',
      boxShadow: `0 0 0 1px ${token.colorBorderSecondary}, 0 1px 2px ${token.colorFillQuaternary}`,
      backgroundColor: token.colorBgContainer,
      '&:hover': {
        boxShadow: `0 0 0 2px ${token.colorPrimary}, 0 2px 8px rgba(0, 0, 0, 0.08)`,
        backgroundColor: token.colorFillTertiary,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  children,
}) => {
  const intl = useIntl();
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const logoutMutation = useLogout();
  const { user } = useAuthStore();
  const { styles } = useStyles();
  const { token } = theme.useToken();

  const initialStateModel = useModel('@@initialState');
  const initialState = initialStateModel as any; // Type assertion for now

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'accountMenuHeader') {
      return;
    }
    if (key === 'logout') {
      flushSync(() => {
        // setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      logoutMutation.mutate();
      return;
    }
    if (key === 'changeInfo') {
      setProfileModalVisible(true);
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;
  
  // Check both Zustand store and initialState for user
  const hasUser = user || currentUser;

  if (!hasUser || (!user?.name && !currentUser?.name)) {
    return loading;
  }

  const displayName = user?.name || currentUser?.name || 'Admin';
  const avatarUrl = currentUser?.avatar;

  const placeholderAvatarStyle = !avatarUrl
    ? {
        background: `linear-gradient(145deg, ${token.colorPrimary} 0%, ${token.colorPrimaryActive} 100%)`,
      }
    : undefined;

  const defaultTrigger = (
    <span className={styles.avatarTrigger} role="button" tabIndex={0} aria-haspopup="true">
      <Avatar
        size={32}
        src={avatarUrl}
        style={placeholderAvatarStyle}
        icon={!avatarUrl ? <UserOutlined style={{ color: '#fff' }} /> : undefined}
      />
    </span>
  );

  const menuItems: MenuProps['items'] = [
    {
      key: 'accountMenuHeader',
      disabled: true,
      className: 'account-dropdown-menu-header',
      icon: <UserOutlined />,
      label: (
        <span
          style={{
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'inline-block',
            maxWidth: 220,
            verticalAlign: 'bottom',
          }}
        >
          {displayName}
        </span>
      ),
    },
    { type: 'divider' },
    {
      key: 'changeInfo',
      icon: <EditOutlined />,
      label: intl.formatMessage({ id: 'pages.userProfile.editInfo' }),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: intl.formatMessage({ id: 'pages.userProfile.logout' }),
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          className: styles.avatarDropdownMenu,
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children ?? defaultTrigger}
      </HeaderDropdown>
      <UserProfileModal
        visible={profileModalVisible}
        onClose={() => setProfileModalVisible(false)}
      />
    </>
  );
};
